import { z } from 'zod';

// Zod schema for form validation
export const confirmAndPaySchema = z.object({
  // Business Location Info
  businessLocationInfo: z
    .object({
      name: z
        .string()
        .min(1, 'Business name is required')
        .max(90, 'Business name must not exceed 90 characters'),
      country: z
        .string()
        .min(1, 'Country is required')
        .refine((val) => val.length === 3, {
          message: 'Country code must be exactly 3 characters'
        })
        .refine((val) => /^[A-Z]{3}$/.test(val), {
          message:
            'Country must be a valid ISO 3 country code (3 uppercase letters)'
        }),
      firstLine: z
        .string()
        .min(1, 'First line is required')
        .max(80, 'First line must not exceed 80 characters'),
      secondLine: z.string().max(80, 'Second line must not exceed 80 characters'),
      townCity: z
        .string()
        .max(100, 'Town/City must not exceed 100 characters'),
      stateCountyRegion: z
        .string()
        .max(100, 'State/County/Region must not exceed 100 characters')
        .optional(),
      postcode: z
        .string()
        .max(80, 'Postcode must not exceed 80 characters'),
      businessPhoneNumber: z
        .string()
        .min(1, 'Business phone number is required')
        .max(20, 'Business phone number must not exceed 20 characters'),
      businessWebsiteUrl: z
        .string()
        .min(1, 'Business website URL is required')
        .max(256, 'Business website URL must not exceed 256 characters')
        .url('Please enter a valid website URL')
    })
    .superRefine((data, ctx) => {
      // Countries where state/County/Region is required
      const requiredCountries = ['USA', 'GBR', 'CAN', 'AUS'];
      // Countries where state/County/Region is optional
      const optionalCountries = [
        'IRL',
        'PHL',
        'NZL',
        'DEU',
        'ZAF',
        'NLD',
        'HKG',
        'MAC',
        'TWN',
        'SGP',
        'FRA'
      ];

      const country = data.country;
      const stateCountyRegion = data.stateCountyRegion || '';

      // If country is in required list, state/County/Region must be provided
      if (requiredCountries.includes(country)) {
        if (!stateCountyRegion || (typeof stateCountyRegion === 'string' && stateCountyRegion.trim().length === 0)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['stateCountyRegion'],
            message: 'State/County/Region is required for this country'
          });
        }
      }
      // For optional countries or other countries, no validation needed (already handled by max length)
    }),
  businessLocationContactInfo: z.object({
    contactFirstName: z
      .string()
      .max(20, 'Contact first name must not exceed 20 characters')
      .refine((val) => !val || /^[a-zA-Z\s'-]+$/.test(val), {
        message:
          'Contact first name should only contain letters, spaces, hyphens, and apostrophes'
      })
      .optional(),
    contactLastName: z
      .string()
      .max(20, 'Contact last name must not exceed 20 characters')
      .refine((val) => !val || /^[a-zA-Z\s'-]+$/.test(val), {
        message:
          'Contact last name should only contain letters, spaces, hyphens, and apostrophes'
      })
      .optional(),
    contactEmail: z.string().refine(
      (value) => {
        // If empty, it's valid (optional field)
        if (!value || value.trim() === '') {
          return true;
        }
        // If provided, validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value);
      },
      {
        message: 'Please enter a valid email address'
      }
    ),
    contactPhoneNumber: z
      .string()
      .max(20, 'Contact phone number must not exceed 20 characters'),
    faxNumber: z.string().max(20, 'Fax number must not exceed 20 characters'),
    openingDate: z
      .any()
      .nullable()
      .optional()
      .refine((value) => value === null || value === undefined || value instanceof Date, {
        message: 'Opening date must be a valid date'
      })
  }),

  // About The Business
  aboutTheBusiness: z.object({
    businessCategory: z
      .object({
        id: z.string().or(z.number()).nullable().optional(),
        name: z.string().min(1, 'Business category is required')
      })
      .nullable()
      .refine((val) => val !== null && val.name.length > 0, {
        message: 'Business category is required'
      }),
    numberOfEmployees: z.string().optional(),
    businessDescription: z
      .string()
      .max(500, 'Business description must not exceed 500 characters'),
    openingHours: z.record(
      z.string(),
      z
        .object({
          status: z
            .string()
            .refine(
              (val) =>
                ['open', 'closed', '24hrs', 'split'].includes(
                  val?.toLowerCase()
                ),
              {
                message: 'Status must be one of: open, closed, 24hrs, split'
              }
            ),
          hours: z.array(
            z.object({
              start: z.string(),
              end: z.string()
            })
          )
        })
        .refine(
          (data) => {
            if (
              data.status?.toLowerCase() === 'open' ||
              data.status?.toLowerCase() === 'split'
            ) {
              return data.hours && data.hours.length > 0;
            }
            return true;
          },
          {
            message: 'Hours are required when status is open or split',
            path: ['hours']
          }
        )
        .refine(
          (data) => {
            if (
              data.status?.toLowerCase() === 'open' ||
              data.status?.toLowerCase() === 'split'
            ) {
              return (
                data.hours?.every(
                  (hour) =>
                    hour.start &&
                    hour.start.length > 0 &&
                    hour.end &&
                    hour.end.length > 0
                ) ?? false
              );
            }
            return true;
          },
          {
            message: 'Start and end times are required for each hour entry',
            path: ['hours']
          }
        )
    ),
    paymentMethods: z
      .array(z.string()),
      // .min(1, 'At least one payment method must be selected'),
    socialMediaLinks: z
      .record(z.string(), z.string())
      .superRefine((socialMediaLinks, ctx) => {
        Object.entries(socialMediaLinks).forEach(([platform, url]) => {
          // Skip validation if URL is empty (optional field)
          if (!url || url.trim() === '') {
            return;
          }
          if (url.length > 256) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [platform],
              message: `${platform} link must not exceed 256 characters`
            });
            return;
          }
          try {
            new URL(url);
          } catch {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [platform],
              message: `Please enter a valid ${platform} URL`
            });
          }
        });
      })
  }),

  // Email Alerts
  emailAlerts: z.string().refine(
    (value) => {
      // If empty, it's valid (optional field)
      if (!value || value.trim() === '') {
        return true;
      }
      // Check max length
      if (value.length > 1000) {
        return false;
      }
      // Split by newlines and filter out empty lines
      const emails = value
        .split('\n')
        .map((email) => email.trim())
        .filter((email) => email.length > 0);

      // Check max 5 emails
      if (emails.length > 5) {
        return false;
      }

      // Validate each email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emails.every((email) => emailRegex.test(email));
    },
    {
      message:
        'Please enter valid email addresses (max 5, one per line). Leave empty if you do not want to receive email alerts.'
    }
  ),

  // Campaign Notes
  campaignNotes: z
    .string()
    .max(1000, 'Campaign notes must not exceed 1000 characters'),

  // Terms & Conditions
  termsAccepted: z.boolean().refine((value) => value === true, {
    message: 'You must accept the terms & conditions to proceed'
  }),

  // Business Images (array of objects with file and label)
  businessImages: z
    .array(
      z.object({
        file: z
          .any()
          .refine((value) => value === null || value instanceof File, {
            message: 'Each image must be a File object or null'
          }),
        label: z.string()
      })
    )
    .length(4, 'Business images array must contain exactly 4 items')
});

export type ConfirmAndPayFormData = z.infer<typeof confirmAndPaySchema>;

// Default values for the form
export const confirmAndPayDefaultValues: ConfirmAndPayFormData = {
  // Business Location Info
  businessLocationInfo: {
    name: '',
    country: '',
    firstLine: '',
    secondLine: '',
    townCity: '',
    stateCountyRegion: '',
    postcode: '',
    businessPhoneNumber: '',
    businessWebsiteUrl: ''
  },
  businessLocationContactInfo: {
    contactFirstName: '',
    contactLastName: '',
    contactEmail: '',
    contactPhoneNumber: '',
    faxNumber: '',
    openingDate: null
  },
  aboutTheBusiness: {
    businessCategory: null,
    numberOfEmployees: '',
    businessDescription: '',
    openingHours: {
      Monday: { status: 'open', hours: [{ start: '', end: '' }] },
      Tuesday: { status: 'open', hours: [{ start: '', end: '' }] },
      Wednesday: { status: 'open', hours: [{ start: '', end: '' }] },
      Thursday: { status: 'open', hours: [{ start: '', end: '' }] },
      Friday: { status: 'open', hours: [{ start: '', end: '16:30' }] },
      Saturday: { status: 'closed', hours: [{ start: '', end: '' }] },
      Sunday: { status: 'closed', hours: [{ start: '', end: '' }] }
    },
    paymentMethods: [],
    socialMediaLinks: {
      Facebook: '',
      Linkedin: '',
      X: '',
      Instagram: '',
      Pinterest: '',
      Youtube: '',
      TikTok: ''
    }
  },
  emailAlerts: '',
  campaignNotes: '',
  termsAccepted: false,
  businessImages: [
    { file: null, label: 'Profile Image' },
    { file: null, label: 'Cover Image' },
    { file: null, label: 'Additional Image 1' },
    { file: null, label: 'Additional Image 2' }
  ]
};
